import { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Target, Repeat, Droplet, ShieldAlert, Sparkles, ChevronRight, ArrowRight } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ExerciseSheet } from "../components/ExerciseSheet";
import { DAYS, PROFILE_META, PROFILES, LEVELS, normalizeProfile, normalizeLevel } from "../config/program";
import { EXERCISE_MAP } from "../config/exercises";
import { ExtDay } from "../components/ExtDay";
import { PRODUCT_URL } from "../lib/constants";

const ROUNDS = {
  Lieve: { n: "1 volta", multi: false },
  Medio: { n: "2 volte", multi: true },
  Alto: { n: "2 volte", multi: true },
};

const PROFILE_SHORT = {
  R: "Ritenzione e microcircolo",
  D: "Addominale e digestivo",
  P: "Sedentarietà e postura",
};

export default function DayPage() {
  const { day } = useParams();
  const dayNum = parseInt(day, 10);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const qp = normalizeProfile(searchParams.get("p"));
  const ql = normalizeLevel(searchParams.get("l"));

  // Scelta rapida (NON salvata) quando i parametri mancano o non sono validi
  const [pickP, setPickP] = useState(null);
  const [pickL, setPickL] = useState(null);

  const [sheetEx, setSheetEx] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const profile = qp || pickP;
  const level = ql || pickL;

  const validDay = dayNum >= 1 && dayNum <= 20;

  if (!validDay) {
    return (
      <PageShell back onBack={() => navigate("/")}>
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <h2 className="font-serif text-2xl font-semibold text-ink">Giorno non valido</h2>
          <p className="mt-3 text-sm text-ink/70">Scegli un giorno da 1 a 20.</p>
        </div>
      </PageShell>
    );
  }

  if (!profile || !level) {
    return (
      <PageShell back onBack={() => navigate("/")}>
        <QuickPicker
          dayNum={dayNum}
          pickP={pickP}
          pickL={pickL}
          setPickP={setPickP}
          setPickL={setPickL}
        />
      </PageShell>
    );
  }

  if (dayNum >= 5) {
    return (
      <PageShell back onBack={() => navigate("/")}>
        <ExtDay profile={profile} level={level} dayNum={dayNum} navigate={navigate} />
      </PageShell>
    );
  }

  const content = DAYS[profile][dayNum - 1];
  const meta = PROFILE_META[profile];
  const rounds = ROUNDS[level];

  const openExercise = (num) => {
    setSheetEx(EXERCISE_MAP[num]);
    setSheetOpen(true);
  };

  return (
    <PageShell back onBack={() => navigate("/")}>
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-1 flex-col pt-1"
        data-testid={`day-page-${dayNum}`}
      >
        <p className="text-xs font-semibold uppercase tracking-wider text-coral">Giorno {dayNum}</p>
        <h2 className="mt-1 font-serif text-3xl font-bold leading-tight text-ink">{content.title}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className="inline-flex items-center rounded-full bg-coral/15 px-3 py-1.5 text-xs font-medium text-coral" data-testid="day-profile">
            {meta.name}
          </span>
          <span className="inline-flex items-center rounded-full bg-ink/10 px-3 py-1.5 text-xs font-medium text-ink/70" data-testid="day-level">
            Il tuo livello: {level}
          </span>
        </div>

        <Section icon={Target} title="Obiettivo del giorno">
          <p>{content.objective}</p>
        </Section>

        <Section icon={Repeat} title={`Routine · ${content.duration}`}>
          <div className="mb-3 rounded-xl bg-coral/10 px-4 py-3 text-sm text-ink/80" data-testid="rounds-note">
            Esegui il giro degli esercizi <strong>{rounds.n}</strong>.
            {rounds.multi && " Gli esercizi a tempo si eseguono una volta sola."}
          </div>
          <div className="flex flex-col gap-2">
            {content.routine.map((item, i) => {
              const ex = EXERCISE_MAP[item.num];
              const dose = item.level_dose ? item.level_dose[level] : item.dose;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => openExercise(item.num)}
                  data-testid={`routine-item-${dayNum}-${i}`}
                  className="flex items-center justify-between rounded-xl bg-white/80 px-4 py-3 text-left shadow-sm transition-colors hover:bg-coral-soft"
                >
                  <span className="flex flex-col pr-2">
                    <span className="text-sm font-semibold text-ink">{ex?.name}</span>
                    {dose && <span className="text-xs text-ink/55">{dose}</span>}
                  </span>
                  <ChevronRight size={18} className="shrink-0 text-coral" />
                </button>
              );
            })}
          </div>

          {level === "Alto" && (
            <div className="mt-4 flex gap-3 rounded-xl border-2 border-coral bg-coral-soft p-4" data-testid="alto-extra">
              <Sparkles size={18} className="mt-0.5 shrink-0 text-coral" />
              <p className="text-sm leading-relaxed text-ink/80">
                <span className="font-semibold text-ink">Extra livello Alto. </span>
                {meta.extra_alto}
              </p>
            </div>
          )}
        </Section>

        <Section icon={Sparkles} title="Abitudine del giorno">
          <p>{content.habit}</p>
        </Section>

        <Section icon={Droplet} title="Alimentazione e idratazione">
          <p>{content.nutrition}</p>
        </Section>

        <div className="mt-5 flex gap-3 rounded-2xl border-l-4 border-coral bg-coral/10 p-4" data-testid="day-safety">
          <ShieldAlert size={20} className="mt-0.5 shrink-0 text-coral" />
          <p className="text-sm leading-relaxed text-ink/80">{meta.safety}</p>
        </div>

        {dayNum === 4 && (
          <div className="mt-7">
            <button
              type="button"
              onClick={() => window.open(PRODUCT_URL, "_blank", "noopener")}
              data-testid="continue-program-button"
              className="w-full rounded-2xl bg-coral px-6 py-4 text-base font-semibold text-white shadow-md shadow-coral/30 transition-all hover:-translate-y-0.5"
            >
              Continua il programma
            </button>
            <p className="mt-3 text-xs leading-relaxed text-ink/55" data-testid="continue-program-note">
              Dopo l'acquisto ricevi un link per attivare il percorso: usa la stessa email del quiz.
              Il giorno 5 arriva alle 8:00 del giorno dopo.
            </p>
          </div>
        )}
      </motion.div>

      <ExerciseSheet exercise={sheetEx} open={sheetOpen} onOpenChange={setSheetOpen} />
    </PageShell>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div className="mt-6">
    <div className="mb-2 flex items-center gap-2">
      <Icon size={18} className="text-coral" />
      <h3 className="font-serif text-lg font-semibold text-ink">{title}</h3>
    </div>
    <div className="text-sm leading-relaxed text-ink/80">{children}</div>
  </div>
);

const QuickPicker = ({ dayNum, pickP, pickL, setPickP, setPickL }) => (
  <div className="flex flex-1 flex-col pt-2" data-testid="quick-picker">
    <h2 className="font-serif text-2xl font-bold leading-tight text-ink">Giorno {dayNum}</h2>
    <p className="mt-3 text-sm leading-relaxed text-ink/70">
      Scegli il tuo profilo e il tuo livello per vedere il contenuto. Questa scelta non viene salvata.
    </p>

    <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-coral">Profilo</p>
    <div className="mt-2 flex flex-col gap-2">
      {PROFILES.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => setPickP(p)}
          data-testid={`pick-profile-${p}`}
          className={`rounded-2xl border-2 px-4 py-3 text-left text-sm font-medium transition-colors ${
            pickP === p ? "border-coral bg-coral-soft text-ink" : "border-transparent bg-white/80 text-ink/80"
          }`}
        >
          {PROFILE_SHORT[p]}
        </button>
      ))}
    </div>

    <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-coral">Livello</p>
    <div className="mt-2 flex gap-2">
      {LEVELS.map((l) => (
        <button
          key={l}
          type="button"
          onClick={() => setPickL(l)}
          data-testid={`pick-level-${l}`}
          className={`flex-1 rounded-2xl border-2 px-4 py-3 text-sm font-medium transition-colors ${
            pickL === l ? "border-coral bg-coral text-white" : "border-ink/15 bg-white text-ink/70"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  </div>
);
